import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import { agentApiClient } from '../utils/agentApiClient';

interface Message {
  id: string;
  type: 'user' | 'agent' | 'error';
  content: string;
  timestamp: Date;
}

interface AgentState {
  isConnected: boolean;
  currentTask: any | null;
  messages: Message[];
  error: string | null;
}

type AgentAction =
  | { type: 'CONNECT' }
  | { type: 'DISCONNECT' }
  | { type: 'SET_TASK'; payload: any }
  | { type: 'ADD_MESSAGE'; payload: { type: 'user' | 'agent' | 'error'; content: string } }
  | { type: 'SET_MESSAGES'; payload: Message[] }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' };

const initialState: AgentState = {
  isConnected: false,
  currentTask: null,
  messages: [],
  error: null
};

const agentReducer = (state: AgentState, action: AgentAction): AgentState => {
  switch (action.type) {
    case 'CONNECT':
      return { ...state, isConnected: true, error: null };
    case 'DISCONNECT':
      return { ...state, isConnected: false };
    case 'SET_TASK':
      return { ...state, currentTask: action.payload };
    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: Math.random().toString(36).substr(2, 9),
            type: action.payload.type,
            content: action.payload.content,
            timestamp: new Date()
          }
        ]
      };
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

interface AgentContextType {
  state: AgentState;
  dispatch: React.Dispatch<AgentAction>;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

export const AgentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(agentReducer, initialState);

  useEffect(() => {
    const loadConversationHistory = async () => {
      try {
        const response = await agentApiClient.getConversationHistory();
        if (response.success && response.data) {
          dispatch({ type: 'SET_MESSAGES', payload: response.data });
        }
      } catch (error) {
        console.error('Error loading conversation history:', error);
      }
    };

    loadConversationHistory();
  }, []);

  return (
    <AgentContext.Provider value={{ state, dispatch }}>
      {children}
    </AgentContext.Provider>
  );
};

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (context === undefined) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
};

export default AgentContext;
